import { useEffect, useLayoutEffect, useRef, useState } from "react";

import classNames from "classnames";

import { ProductData } from "data/product/products";

import { button } from "~/components/BTSInProductVideoPage/SeriesDetails/SeriesDetails.css";
import { CreatorInfo } from "~/components/common/CreatorInfo";
import { ManIcon } from "~/components/common/Icons/ManIcon";
import { StarReviewsIcon } from "~/components/common/Icons/StarReviewsIcon";
import { ImageWithBlur } from "~/components/common/ImageWithBlur/ImageWithBlur";
import { OverlayModal } from "~/components/common/OverlayModal";
import { TopicsSection } from "~/components/videoProductPage/components/VideoSection/TopicsSection/TopicsSection";
import {
  productContainer,
  videoDetails,
  separatorPoint,
  videoReviews,
  rating,
  reviewsFullNumbers,
  videoDuration,
  videoDate,
  descriptionBlock,
  description,
  videoSectionShowMore,
  videoSectionShowLess,
  videoAuthorResponsive,
} from "~/components/videoProductPage/components/VideoSection/VideoSection.css";
import { isScrollAvailable } from "~/util/sizeUtils";
import {
  addLinksToText,
  convertSecondsToFriendlyDuration,
  displayNumberWithCommas,
  formatFriendlyDate,
  magicConversion,
} from "~/util/stringUtils";

import * as styles from "./ProductDetailsModal.css";

const DESKTOP_CHARACTER_COUNT_LIMIT = 280;

export const ProductDetailsModal = ({
  creatorDescription,
  productData,
  isDetailsModalOpen,
  closeDetailsModal,
}: {
  creatorDescription: string;
  productData: ProductData;
  isDetailsModalOpen: boolean;
  closeDetailsModal: () => void;
}) => {
  const descriptionRef = useRef(null);
  const [hasScroll, setHasScroll] = useState(false);
  const [scrollAvailable, setScrollAvailable] = useState(true);
  const element = document.getElementById("description-contain");
  const [truncatedText, setTruncatedText] = useState({
    content: productData.description,
    truncated: false,
  });

  useEffect(() => {
    if (productData.description.length <= DESKTOP_CHARACTER_COUNT_LIMIT) {
      setTruncatedText({ content: productData.description, truncated: false });
      return;
    }

    const truncatedText = productData.description.slice(0, DESKTOP_CHARACTER_COUNT_LIMIT) + "...";
    setTruncatedText({
      content: truncatedText,
      truncated: true,
    });
  }, [productData.description]);

  useEffect(() => {
    if (element) {
      setHasScroll(element.scrollHeight > element.clientHeight);
    }
  }, [element]);

  return (
    <OverlayModal
      isOpen={isDetailsModalOpen}
      onClose={closeDetailsModal}
      contentClass={styles.modalContent}
    >
      <div>
        <h3 className={styles.modalTitle}>{productData.title}</h3>
        <div className={classNames(styles.creatorContainer, videoAuthorResponsive)}>
          <CreatorInfo
            name={productData.creatorName}
            avatar={productData.creatorProfileImageURL}
            slug={productData.storefrontSlug}
          />
        </div>
        <div className={classNames(styles.modalDetails, productContainer, videoDetails)}>
          <div className={styles.desktopAuthorContainer}>
            <CreatorInfo
              name={productData.creatorName}
              avatar={productData.creatorProfileImageURL}
              slug={productData.storefrontSlug}
            />
          </div>
        </div>
        <div className={styles.relativeContainer}>
          <div
            id="description-contain"
            ref={descriptionRef}
            onScroll={() => {
              setScrollAvailable(isScrollAvailable(descriptionRef.current));
            }}
            className={classNames(styles.modalDescriptionBlock, productContainer, descriptionBlock)}
          >
            <p className={classNames(styles.modalDescription, description)}>
              {truncatedText.truncated && (
                <div
                  dangerouslySetInnerHTML={{ __html: magicConversion(productData.description) }}
                ></div>
              )}
            </p>
          </div>
          {hasScroll && scrollAvailable && <div className={styles.modalDescriptionBlockOverlay} />}
        </div>
        <div className={styles.aboutCreator}>
          <div className={styles.productAuthorImage}>
            <ImageWithBlur
              src={productData.creatorProfileImageURL}
              layout={"fill"}
              objectFit={"cover"}
              style={{ borderRadius: "8px", objectFit: "cover" }}
            />
          </div>
          <div>
            <h4 className={styles.creatorName}>About {productData.creatorName}</h4>
            <div className={styles.creatorDescriptionContainer}>
              <p className={styles.creatorDescription}>{creatorDescription}</p>{" "}
            </div>
          </div>
        </div>
        <a href={`/classes/${productData.storefrontSlug}`} target="_blank" rel="noreferrer">
          <button
            className={classNames(styles.creatorButton, button)}
          >{`Go to ${productData.creatorName}'s Studio`}</button>
        </a>
      </div>
    </OverlayModal>
  );
};
